import { DefinitionOfDoneCriteria } from '../components/journey/DefinitionOfDone';

/**
 * Generates contextual verification survey questions based on journey step
 * @param stepTitle - The title of the journey step
 * @param stepIndex - The index of the journey step
 * @param t - Optional translation function. If not provided, uses English text.
 */
export function generateVerificationSurvey(
  stepTitle: string,
  stepIndex: number,
  t?: (key: string) => string
): DefinitionOfDoneCriteria {
  return {
    id: 'verification-survey',
    title: t ? t('definitionOfDone.verificationSurvey.title') : 'Completion Verification',
    description: t ? t('definitionOfDone.verificationSurvey.description') : 'Confirm that you have: 1) Created validation survey 2) Collected minimum 10+ responses 3) Validated idea with 40%+ problem frequency',
    required: true,
    completed: false,
    validationMethod: 'manual', // Simple checkbox, no survey questions
    helpText: t ? t('definitionOfDone.verificationSurvey.helpText') : 'Check this box only after all above criteria are completed and verified.',
    disabled: true // Start disabled by default, will be enabled when dependencies are met
  };
}



/**
 * Add verification survey to existing criteria array
 * @param criteria - Existing criteria array
 * @param stepTitle - The title of the journey step
 * @param stepIndex - The index of the journey step
 * @param options - Optional configuration including dependencies and translation function
 */
export function addVerificationSurvey(
  criteria: DefinitionOfDoneCriteria[],
  stepTitle: string,
  stepIndex: number,
  options?: {
    dependsOn?: string; // ID of criterion that must be completed first
    disabledReason?: string; // Custom reason for being disabled
    t?: (key: string) => string; // Translation function
  }
): DefinitionOfDoneCriteria[] {
  // Check if verification survey already exists
  const hasVerification = criteria.some(c => c.id === 'verification-survey');

  if (hasVerification) {
    return criteria;
  }

  // Create the verification survey with optional translation
  const survey = generateVerificationSurvey(stepTitle, stepIndex, options?.t);

  // If there's a dependency, check if it's completed
  if (options?.dependsOn) {
    const dependentCriterion = criteria.find(c => c.id === options.dependsOn);
    const isDepCompleted = dependentCriterion?.completed || false;

    // Set disabled state based on dependency completion
    survey.disabled = !isDepCompleted;
    survey.disabledReason = options.disabledReason ||
      `Complete "${dependentCriterion?.title || 'previous criterion'}" first to unlock this verification`;
  } else {
    // If no dependency specified, enable by default
    survey.disabled = false;
  }

  // Add verification survey as the last criterion
  return [...criteria, survey];
}

/**
 * Update verification survey disabled state based on dependencies
 */
export function updateVerificationSurveyState(
  criteria: DefinitionOfDoneCriteria[],
  dependsOnId?: string
): DefinitionOfDoneCriteria[] {
  if (!dependsOnId) return criteria;

  return criteria.map(criterion => {
    if (criterion.id === 'verification-survey') {
      const dependentCriterion = criteria.find(c => c.id === dependsOnId);
      const isDepCompleted = dependentCriterion?.completed || false;



      return {
        ...criterion,
        disabled: !isDepCompleted
      };
    }
    return criterion;
  });
}
